/**
 * WHO DISPOSED OF THE TOXIC WASTE, AND UNDER WHAT NUMBER.
 *
 * Gap 3 of five from the 2026-09-16 compliance audit. There is a live
 * "Hazardous waste disposal" line on the book and no EPA identification
 * number anywhere in the system.
 *
 * ─── WHAT IS VERIFIED AND WHAT IS INHERITED ────────────────────────────
 *
 * Verified by reading the code and the book on 2026-09-18: the charge
 * exists, nothing stores an EPA id, nothing prints one.
 *
 * Inherited from the earlier audit note and NOT re-read against the
 * regulation on 2026-09-18: that the id belongs on the invoice beside the
 * charge. The itemization half is not in doubt and is the same rule that
 * makes a generic "Misc Shop Supplies" line prohibited. Whoever picks
 * this up next should check the second half rather than trust it because
 * it is written down confidently here.
 *
 * ─── THE MODULE NEVER INVENTS A NUMBER ─────────────────────────────────
 *
 * Every function here takes the id as an argument and returns nothing
 * when it is absent. There is no constant, no default and no derivation
 * from the address or the BAR registration. A wrong EPA id on an invoice
 * is worse than none, because an absent one is obviously absent and a
 * wrong one identifies some other registered generator to a regulator.
 *
 * As of 2026-09-18 no shop has one set. Anthony is looking Supercanic's
 * up. Everything below behaves correctly in the meantime, which is the
 * test of whether it was written honestly.
 *
 * Pure, no imports, shared with the server -- like partcondition.ts.
 */

/**
 * Words that mean "this line is a disposal charge".
 *
 * Matched against the free text description, because that is what the
 * book actually holds: there is no disposal flag on a line and adding one
 * would leave 272 existing lines unflagged and looking clean.
 *
 * Kept deliberately narrow. "oil" alone would catch every oil change,
 * which is not a disposal charge, and a nag that fires on a third of the
 * book is a nag everybody learns to scroll past.
 */
const DISPOSAL_WORDS = [
  "hazardous waste",
  "hazmat",
  "haz waste",
  "toxic waste",
  "waste disposal",
  "disposal fee",
  "epa fee",
  "environmental fee",
  "oil disposal",
  "coolant disposal",
  "refrigerant recovery",
  "tire disposal",
  "battery disposal",
  "core charge disposal",
];

/** Whether one line's description reads as a disposal charge. */
export function isDisposalLine(description: unknown): boolean {
  const d = String(description ?? "").toLowerCase();
  if (!d.trim()) return false;
  return DISPOSAL_WORDS.some((w) => d.includes(w));
}

/**
 * The disposal lines on a job, as a list rather than a boolean.
 *
 * Same reasoning as partsMissingCondition: "the hazardous waste line has
 * no EPA number behind it" is actionable and "this job is not compliant"
 * is not.
 */
export function disposalLines<
  T extends { description?: string | null },
>(lines: readonly T[]): T[] {
  return lines.filter((l) => isDisposalLine(l.description));
}

/** Tidy an id for display without changing what it says. */
export function epaId(v: unknown): string | null {
  const s = String(v ?? "").trim().toUpperCase();
  return s.length ? s : null;
}

/**
 * The line printed under a disposal charge on the invoice, or null.
 *
 * Null in two different situations, and the caller does not need to tell
 * them apart: the job has no disposal charge, or the shop has no id. In
 * both cases the invoice says nothing, which is correct -- an invoice
 * that announces "EPA identification number: not provided" invites a
 * question the customer cannot do anything with.
 *
 * The desk is told about the second case instead, by epaNag() below,
 * because the desk can fix it.
 */
export function disposalNote<
  T extends { description?: string | null },
>(lines: readonly T[], shopEpaId: unknown): string | null {
  if (!disposalLines(lines).length) return null;
  const id = epaId(shopEpaId);
  if (!id) return null;
  return `Hazardous waste from this job was handled under EPA identification `
    + `number ${id}.`;
}

/**
 * What the invoice screen tells the advisor, or null when all is well.
 *
 * REPORTS, DOES NOT BLOCK -- the same call papersNeedingConsent() makes.
 * The invoice still sends. A shop that has just started charging a
 * disposal fee on a Friday afternoon should not be unable to bill anybody
 * until the owner finds a certificate in a drawer.
 */
export function epaNag<
  T extends { description?: string | null },
>(lines: readonly T[], shopEpaId: unknown): string | null {
  const charges = disposalLines(lines);
  if (!charges.length) return null;
  if (epaId(shopEpaId)) return null;
  const which = charges
    .map((l) => String(l.description ?? "").trim())
    .filter(Boolean)
    .slice(0, 2)
    .join(", ");
  return `This invoice charges for waste disposal${which ? ` (${which})` : ""} `
    + `and the shop has no EPA identification number saved, so the invoice `
    + `cannot name who disposed of it. Add it once under Settings and every `
    + `invoice from then on carries it.`;
}
